import React, { useState, useRef } from 'react';
import { 
  Camera, 
  CloudUpload, 
  X, 
  Check, 
  AlertCircle,
  FileImage,
  Trash2
} from 'lucide-react';
import API_URL from '../config/ApiUrl';

const CoverImageUploadDialog = ({ 
  isOpen, 
  onClose, 
  onUploadSuccess, 
  currentImageUrl = null 
}) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const validateFile = (selectedFile) => {
    const maxSizeMB = 5;
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    
    if (!allowedTypes.includes(selectedFile.type)) {
      return 'Please select a valid image file (JPEG, PNG, GIF, WebP)';
    }
    
    if (selectedFile.size > maxSizeMB * 1024 * 1024) {
      return `File size must be less than ${maxSizeMB}MB`;
    }
    
    return null;
  };

  const handleFileChange = (selectedFile) => {
    if (!selectedFile) return;
    
    const validationError = validateFile(selectedFile);
    if (validationError) {
      setError(validationError);
      return;
    }
    
    setFile(selectedFile);
    setError('');
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleInputChange = (e) => {
    handleFileChange(e.target.files[0]);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const droppedFile = e.dataTransfer.files[0];
    handleFileChange(droppedFile);
  };

  const clearFile = () => {
    setFile(null);
    setPreviewUrl('');
    setError('');
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const simulateProgress = () => {
    return new Promise((resolve) => {
      let progress = 0;
      const interval = setInterval(() => {
        progress += Math.random() * 30;
        if (progress >= 100) {
          progress = 100;
          clearInterval(interval);
          resolve();
        }
        setUploadProgress(Math.min(progress, 100));
      }, 200);
    });
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('cover_image', file);

      // Start progress simulation
      const progressPromise = simulateProgress();

      // Make the upload request - you'll need to replace this URL with your actual endpoint
      const response = await fetch(`${API_URL}/api/profile/upload-cover-image`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      // Wait for progress to complete
      await progressPromise;

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const result = await response.json();
      
      if (result.success) {
        // Call success callback with the new image URL
        onUploadSuccess(result.cover_image_url);
        // Close dialog and reset state
        onClose();
        clearFile();
      } else {
        throw new Error(result.error || 'Upload failed');
      }
      
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveCoverImage = async () => {
    try {
      setUploading(true);
      
      const response = await fetch('/api/profile/remove-cover-image', {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Failed to remove cover image');
      }

      const result = await response.json();
      
      if (result.success) {
        onUploadSuccess(null); // Pass null to indicate removal
        onClose();
      } else {
        throw new Error(result.error || 'Failed to remove cover image');
      }
      
    } catch (err) {
      console.error('Remove cover image error:', err);
      setError(err.message || 'Failed to remove cover image');
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
      backdropFilter: 'blur(10px)'
    }}>
      <div style={{
        background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
        borderRadius: '24px',
        width: '100%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'hidden',
        border: '1px solid #333',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)'
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
          padding: '20px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Camera size={24} style={{ color: 'white' }} />
            <h2 style={{
              margin: 0,
              fontSize: '20px',
              fontWeight: '700',
              color: 'white'
            }}>
              Update Cover Photo
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '12px',
              padding: '8px',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleInputChange}
            style={{ display: 'none' }}
          />

          {/* Current Cover Image */}
          {currentImageUrl && !file && (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{
                margin: '0 0 12px 0',
                fontSize: '16px',
                fontWeight: '600',
                color: '#e5e5e5'
              }}>
                Current Cover Photo
              </h3>
              <div style={{ position: 'relative' }}>
                <img
                  src={currentImageUrl}
                  alt="Current cover"
                  style={{
                    width: '100%',
                    height: '150px',
                    objectFit: 'cover',
                    borderRadius: '16px',
                    border: '2px solid #333'
                  }}
                />
                <button
                  onClick={handleRemoveCoverImage}
                  disabled={uploading}
                  style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: 'rgba(239, 68, 68, 0.9)',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '8px',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '12px',
                    fontWeight: '500',
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  <Trash2 size={14} />
                  Remove
                </button>
              </div>
            </div>
          )}

          {!file ? (
            /* Upload Area */
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: `3px dashed ${dragActive ? '#00d4aa' : '#444'}`,
                borderRadius: '20px',
                padding: '40px 20px',
                textAlign: 'center',
                backgroundColor: dragActive ? 'rgba(0, 212, 170, 0.1)' : '#2a2a2a',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                marginBottom: '20px'
              }}
            >
              <CloudUpload 
                size={48} 
                style={{ 
                  color: dragActive ? '#00d4aa' : '#666',
                  marginBottom: '16px'
                }} 
              />
              <h3 style={{
                margin: '0 0 8px 0',
                fontSize: '18px',
                fontWeight: '600',
                color: '#e5e5e5'
              }}>
                {dragActive ? 'Drop your image here' : 'Upload Cover Photo'}
              </h3>
              <p style={{
                margin: '0 0 8px 0',
                fontSize: '14px',
                color: '#888'
              }}>
                Click to browse or drag and drop your image
              </p>
              <p style={{
                margin: 0,
                fontSize: '12px',
                color: '#666'
              }}>
                Supports JPEG, PNG, GIF, WebP • Max 5MB
              </p>
            </div>
          ) : (
            /* File Preview and Upload */
            <div>
              {/* Preview */}
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{
                  margin: '0 0 12px 0',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#e5e5e5'
                }}>
                  Preview
                </h3>
                <div style={{ position: 'relative' }}>
                  <img
                    src={previewUrl}
                    alt="Preview"
                    style={{
                      width: '100%',
                      height: '200px',
                      objectFit: 'cover',
                      borderRadius: '16px',
                      border: '2px solid #333'
                    }}
                  />
                  <button
                    onClick={clearFile}
                    style={{
                      position: 'absolute',
                      top: '12px',
                      right: '12px',
                      background: 'rgba(0, 0, 0, 0.7)',
                      border: 'none',
                      borderRadius: '12px',
                      padding: '8px',
                      color: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>

              {/* File Info */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px',
                background: '#333',
                borderRadius: '12px',
                marginBottom: '20px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <FileImage size={20} style={{ color: '#00d4aa' }} />
                  <div>
                    <p style={{
                      margin: 0,
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#e5e5e5'
                    }}>
                      {file.name}
                    </p>
                    <p style={{
                      margin: 0,
                      fontSize: '12px',
                      color: '#888'
                    }}>
                      {(file.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <button
                  onClick={clearFile}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#888',
                    cursor: 'pointer',
                    padding: '4px'
                  }}
                >
                  <X size={16} />
                </button>
              </div>

              {/* Upload Progress */}
              {uploading && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '8px'
                  }}>
                    <span style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#e5e5e5'
                    }}>
                      Uploading...
                    </span>
                    <span style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#00d4aa'
                    }}>
                      {Math.round(uploadProgress)}%
                    </span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '8px',
                    backgroundColor: '#333',
                    borderRadius: '12px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${uploadProgress}%`,
                      height: '100%',
                      background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                      borderRadius: '12px',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 16px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '12px',
              marginBottom: '20px'
            }}>
              <AlertCircle size={16} style={{ color: '#ef4444' }} />
              <span style={{
                fontSize: '14px',
                color: '#ef4444'
              }}>
                {error}
              </span>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div style={{
          padding: '20px 24px',
          borderTop: '1px solid #333',
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            disabled={uploading}
            style={{
              padding: '12px 24px',
              background: 'transparent',
              border: '2px solid #444',
              borderRadius: '12px',
              color: '#888',
              cursor: uploading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              opacity: uploading ? 0.5 : 1
            }}
          >
            Cancel
          </button>
          
          {file && (
            <button
              onClick={handleUpload}
              disabled={uploading}
              style={{
                padding: '12px 24px',
                background: uploading 
                  ? 'linear-gradient(135deg, #666 0%, #555 100%)'
                  : 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                border: 'none',
                borderRadius: '12px',
                color: 'white',
                cursor: uploading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.3s ease'
              }}
            >
              {uploading ? (
                <>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  Uploading...
                </>
              ) : (
                <>
                  <Check size={16} />
                  Upload Cover Photo
                </>
              )}
            </button>
          )}
        </div>
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};
export default CoverImageUploadDialog;